import { faqs, howItWorks, AnalyticsHeader } from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/orlando-city-logo.png";
const Banner =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/2026_City_FlexPack_WorldCup_V21486x148_WebHeader.jpg";
const Background =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/orlando-city-background.jpg";
const seatMap3dPreviewImage =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/orlando-city-seating-chart.jpg";

const config: TeamConfig = {
  teamSchema: "orlandocitysc_flex",
  browserTitle: "Orlando City SC Flex Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Orlando City SC",

  buckets: {
    enabled: true,
    prioritizeMinGameSelectionCount: true,
    list: [
      {
        id: 1,
        min: 1,
        max: 1,
        name: "Welcome Back Lions & Antoine Griezmann's First Home Match Eligible for Selection",
        isPrimary: true,
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
        description: "Select the match to continue.",
        remainingBucketLanguage:
          "Select 3 games to complete your 3-Match Pack!",
      },
      {
        id: 2,
        min: 2,
        max: 3,
        name: "Select 2",
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
        description: "Add two more matches to continue.",
        remainingBucketLanguage:
          "Select 2 more games to complete your 3-Match Pack!",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10199-orlandocitysc-sec",
    mapId: "blockmap2d",
    sectionNameMapping: {
      SUPPORTERS: ["GA1", "GA2", "GA3", "GA4", "GA5", "GA6", "GA7"],
    },
    isView3dButtonHiddenBySectionName: [
      "SUPPORTERS",
      "GA1",
      "GA2",
      "GA3",
      "GA4",
      "GA5",
      "GA6",
      "GA7",
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,

    defaultSeatCount: 2,
    isPerksEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl:
      "https://flex.attendevents.com/orlandocitysc_flex/events_home",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/ocsoccer/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage,
    faviconImage: logoImage,
    findGamesBannerImage: Banner,
    authBackgroundImage: Background,
    checkoutStatusPageImage: Background,
    logoImage: `${logoImage}?width=64&height=64`,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
      eventCardMaxWidth: 350, // in pixels
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,
    },

    checkoutPage: {
      checkoutDisclaimer:
        "If you purchased General Admission tickets in the Supporters Section, please note that visiting team apparel is not permitted.",
    },
  },

  colors: {
    primary: "#61259E",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarSuccessText:
        "You're good to go! Click Continue to view seating options.",
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: "Thank you for your Orlando City SC purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
